import type { PolygonWithHoles, Ring, Vec2 } from '../../../../geo/types';
import type { Frame, MeshBuilder } from '../../../mesh/meshBuilder';
import type { BespokeGate, GateBlock, GatePassage } from '../../fortifications/bespokeGate';
import { layoutToWorld } from '../../../../core/coords';
import { difference, fromRing, union } from '../../../../geo/clip';
import { prismSolid } from '../../solids';
import { merlonsAlong, type FortressStyles } from './fortressParts';
import { archedBlock, gableHipRoof } from './fortressBlocks';
import { waagStone } from './sintAntoniespoortStone';
import { bigTower, stairTowers, towerFeet, voorpoortTower } from './sintAntoniespoortTowers';
import { ROOF_START, friezes, openingsOfTheFronts, steppedGable } from './sintAntoniespoortDetails';
import {
  ANTONIESPOORT_FRAME,
  BIG_TOWERS,
  CHIMNEYS,
  CULVERT_WALLS,
  FOOT,
  HOOFDPOORT,
  MERLON_DEPTH,
  PASSAGE,
  VOORPOORT,
  VOORPOORT_TOWERS,
  rectRing,
} from './sintAntoniespoortPlan';

export const SINT_ANTONIESPOORT_ID = 'gate-sint-antoniespoort';

const AXIS = (PASSAGE.v[0] + PASSAGE.v[1]) / 2;
const ENDS = [HOOFDPOORT.u[0], VOORPOORT.u[1]] as const;

/** A plan point (u along the passage toward the field, v to its left) in layout coordinates. */
export function planToLayout(p: Vec2): Vec2 {
  const { origin, heading } = ANTONIESPOORT_FRAME;
  const [c, s] = [Math.cos(heading), Math.sin(heading)];
  return [origin[0] + p[0] * c - p[1] * s, origin[1] + p[0] * s + p[1] * c];
}

/* The foot of the gate's centre, halfway along its passage, moves the whole gate in the painter view. */
export const ANTONIESPOORT_ANCHOR: Vec2 = planToLayout([(ENDS[0] + ENDS[1]) / 2, AXIS]);

/* Banded to bandsTo but for the Geldersekade front (v[1]), plain brick above. */
function hoofdpoortWalls(builder: MeshBuilder, styles: FortressStyles, frame: Frame): void {
  const { u, v, walk, bandsTo } = HOOFDPOORT;
  const lower = { u, v, foot: FOOT, top: bandsTo, passage: PASSAGE, openTop: true };
  archedBlock(builder, waagStone(styles).gobertange, frame, lower, { v1: styles.wall });
  prismSolid(builder, styles.wall, frame, rectRing(u, v), bandsTo, walk, {
    top: true,
    bottom: false,
  });
}

function hoofdpoort(builder: MeshBuilder, styles: FortressStyles, frame: Frame): void {
  const { u, v, walk, merlonTop, merlons, roof } = HOOFDPOORT;
  hoofdpoortWalls(builder, styles, frame);
  const spec = { ...merlons, depth: MERLON_DEPTH, y0: walk, y1: merlonTop };
  for (const line of [
    { from: [u[0], v[0]], to: [u[1], v[0]] },
    { from: [u[1], v[0]], to: [u[1], v[1]] },
    { from: [u[1], v[1]], to: [u[0], v[1]] },
  ] as const) {
    merlonsAlong(builder, styles.wall, frame, line, spec);
  }
  const roofU = [ROOF_START, roof.u[1]] as const;
  gableHipRoof(builder, styles, frame, { u: roofU, v: roof.v, eave: walk, ridge: roof.ridge });
  steppedGable(builder, styles.wall, frame);
  for (const chimney of CHIMNEYS) {
    const ring = rectRing(chimney.u, chimney.v);
    prismSolid(builder, styles.wall, frame, ring, walk, chimney.top, { top: true, bottom: false });
  }
}

/* The walls over the culvert and the voorpoort, crenellated on the sides that face out. */
function forework(builder: MeshBuilder, styles: FortressStyles, frame: Frame): void {
  const { u, v, walk, merlonTop, merlons } = CULVERT_WALLS;
  const spec = { ...merlons, depth: MERLON_DEPTH, y0: walk, y1: merlonTop };
  const capped = { top: true, bottom: false };
  for (const side of v)
    prismSolid(builder, styles.wall, frame, rectRing(u, side), FOOT, walk, capped);
  const passage = { v: PASSAGE.v, springing: VOORPOORT.springing };
  archedBlock(builder, styles.wall, frame, { ...VOORPOORT, foot: FOOT, top: walk, passage });
  const [west, east] = [v[0][0], v[1][1]];
  const field = VOORPOORT.u[1];
  for (const line of [
    { from: [u[0], west], to: [u[1], west] },
    { from: [field, VOORPOORT.v[0]], to: [field, VOORPOORT.v[1]] },
    { from: [u[1], east], to: [u[0], east] },
  ] as const) {
    merlonsAlong(builder, styles.wall, frame, line, spec);
  }
}

/** The gate of 1538 on its plan (sintAntoniespoortPlan.ts), in the layout's own frame. */
export function buildSintAntoniespoort(builder: MeshBuilder, styles: FortressStyles): void {
  const frame: Frame = {
    origin: layoutToWorld(ANTONIESPOORT_FRAME.origin, 0),
    yaw: ANTONIESPOORT_FRAME.heading,
  };
  hoofdpoort(builder, styles, frame);
  forework(builder, styles, frame);
  for (const tower of BIG_TOWERS) bigTower(builder, styles, frame, tower);
  for (const tower of VOORPOORT_TOWERS) voorpoortTower(builder, styles, frame, tower);
  stairTowers(builder, styles, frame);
  friezes(builder, styles, frame);
  openingsOfTheFronts(builder, styles, frame);
}

function toLayout(piece: PolygonWithHoles): PolygonWithHoles {
  return {
    outer: piece.outer.map(planToLayout),
    holes: piece.holes.map((h) => h.map(planToLayout)),
  };
}

/* Joined in the plan's own coordinates, where the parts that meet share their edges exactly. */
function masonry(): readonly PolygonWithHoles[] {
  const rings: Ring[] = [
    rectRing(HOOFDPOORT.u, HOOFDPOORT.v),
    ...CULVERT_WALLS.v.map((v) => rectRing(CULVERT_WALLS.u, v)),
    rectRing(VOORPOORT.u, VOORPOORT.v),
    ...towerFeet(BIG_TOWERS, VOORPOORT_TOWERS),
  ];
  return union(rings.map(fromRing));
}

/* The culvert's mouths stand this far out of the walls over it, at the water. */
const MOUTH_REACH = 0.05;

/** The whole plan, its passage and the court over the culvert included: no lot reaches into it. */
export function antoniespoortFootprint(): PolygonWithHoles {
  const [west, east] = [CULVERT_WALLS.v[0][0], CULVERT_WALLS.v[1][1]];
  const court = rectRing(CULVERT_WALLS.u, [west - MOUTH_REACH, east + MOUTH_REACH]);
  const whole = union([...masonry(), fromRing(court)]);
  if (whole.length !== 1) throw new Error(`The gate's plan falls in ${whole.length} pieces`);
  return toLayout(whole[0]!);
}

/** The masonry less the passage (PLAN, Obstacles): one piece either side of it. */
export function antoniespoortObstacles(): PolygonWithHoles[] {
  const way = rectRing([ENDS[0] - 0.1, ENDS[1] + 0.1], PASSAGE.v);
  return difference(masonry(), [fromRing(way)]).map(toLayout);
}

export function antoniespoortPassage(): GatePassage {
  return {
    from: planToLayout([ENDS[0], AXIS]),
    to: planToLayout([ENDS[1], AXIS]),
    halfWidth: (PASSAGE.v[1] - PASSAGE.v[0]) / 2,
  };
}

/**
 * The town walls meet the hoofdpoort's long sides about 2.6 m behind its city front: the southern
 * wall (S 4) its west face and the northern wall (S 31) its east face (AAR 95 fig. 18).
 */
export function antoniespoortBlock(): GateBlock {
  const { u, v } = HOOFDPOORT;
  return {
    centre: planToLayout([(u[0] + u[1]) / 2, (v[0] + v[1]) / 2]),
    heading: ANTONIESPOORT_FRAME.heading,
    half: [(u[1] - u[0]) / 2, (v[1] - v[0]) / 2],
  };
}

export const SINT_ANTONIESPOORT: BespokeGate = {
  anchor: ANTONIESPOORT_ANCHOR,
  build: buildSintAntoniespoort,
  footprint: antoniespoortFootprint,
  obstacles: antoniespoortObstacles,
  passage: antoniespoortPassage,
  block: antoniespoortBlock,
};
